import { concat, each, filter, flatMap, reject } from 'lodash';
import React from 'react';
import uuidv4 from 'uuid/v4';

import {
  setProjectAnalysisLayers,
  setProjectAnalysisLayersSorting,
  setProjectAnalysisVisibleLayerIds,
  setProjectAnalysisProcessingStep,
  setProjectAnalysisIsProcessing,
  setProjectExports
} from './layersActions';

import { PROJECTS_TAG } from '../../../projects/data/actions';
import { fetchProjectAnalysisStimuli, setStimuliSortingIds } from './stimuliActions';
import { fetchProjectAnalysisEvents } from './eventsActions';
import {
  fetchProjectAnalysisSignals,
  setProjectAnalysisSignalColorsFromLayers,
  setSignalsSortingIds
} from './signalsActions';
import {
  updateProjectAnalysisDataAggregateJourneyMap,
  updateProjectAnalysisDataCircumplex,
  updateProjectAnalysisDataHeatmaps,
  updateProjectAnalysisDataJourneyMap,
  updateProjectAnalysisDataVideo,
  setAnalysisDataSet
} from './analysisDataActions';

import VisualisationsHelper from '../../lib/VisualisationsHelper';
import { fetchAnnotations } from './annotationsActions';

export const LAYERS_TAG = 'Layers';

const download = (filename, content) => {
  const element = document.createElement('a');
  element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(content)}`);
  element.setAttribute('download', filename);

  element.style.display = 'none';
  document.body.appendChild(element);

  element.click();

  document.body.removeChild(element);
};

/*
 Layer Sorting Order Actions
 */

const setLayersSortingIds = layers => {
  return (dispatch, getState) => {
    const { layersSorting } = getState().analysis.single;
    const rejected = reject(layers, l => {
      return layersSorting.indexOf(l.id) >= 0;
    }).map(layer => {
      return layer.id;
    });
    dispatch(setProjectAnalysisLayersSorting(concat(layersSorting, rejected)));
  };
};

export const fetchProjectAnalysisLayers = projectId => {
  return async (dispatch, getState, { apiClient }) => {
    const results = await apiClient.call(PROJECTS_TAG, 'getProjectLayers', {
      projectId,
      $limit: 100
    });

    dispatch(setProjectAnalysisLayers(results.data));
    dispatch(setProjectAnalysisSignalColorsFromLayers(results.data));
    dispatch(setLayersSortingIds(results.data));
    dispatch(fetchProjectAnalysisSignals(projectId));
    dispatch(fetchProjectAnalysisStimuli(projectId));
    dispatch(fetchProjectAnalysisEvents(projectId));
  };
};

export const createNewLayer = projectId => {
  return async (dispatch, getState, { apiClient }) => {
    await apiClient.call(LAYERS_TAG, 'createLayer', null, {
      project_id: projectId,
      name: `Layer ${getState().analysis.single.layers.length + 1}`,
      layer_occurrence_associations: [],
      layer_participation_associations: [],
      layer_group_participations_associations: [],
      layer_abstract_type_associations: []
    });
  };
};

export const deleteLayer = layer => {
  return async (dispatch, getState, { apiClient }) => {
    await apiClient.call(LAYERS_TAG, 'deleteLayer', { id: layer.id });
  };
};

export const updateLayer = layer => {
  return async (dispatch, getState, { apiClient }) => {
    await apiClient.call(LAYERS_TAG, 'updateLayer', { id: layer.id }, layer);
  };
};

export const updateProjectAnalysisLayerInStore = payload => {
  return (dispatch, getState) => {
    let newLayers;

    if (payload.operation === 'remove') {
      newLayers = filter(getState().analysis.single.layers, layer => {
        return layer.id !== payload.data.id;
      });
    } else {
      let isNew = true;
      newLayers = getState().analysis.single.layers.map(layer => {
        if (payload.data.id === layer.id) {
          isNew = false;
          return payload.data;
        }
        return layer;
      });
      if (isNew) {
        newLayers.push(payload.data);
      }
    }

    dispatch(setProjectAnalysisLayers(newLayers));
    dispatch(setProjectAnalysisSignalColorsFromLayers(newLayers));
    dispatch(setLayersSortingIds(newLayers));
    dispatch(setSignalsSortingIds(getState().analysis.single.signals));
    dispatch(setStimuliSortingIds(getState().analysis.single.stimuli));
  };
};

export const setLayerVisibility = (id, visible = true) => {
  return (dispatch, getState) => {
    const visibleLayerIds = getState().analysis.single.visibleLayerIds.slice();

    if (visibleLayerIds.indexOf(id) >= 0 && !visible) {
      visibleLayerIds.splice(visibleLayerIds.indexOf(id), 1);
    } else if (visible && visibleLayerIds.indexOf(id) < 0) {
      visibleLayerIds.push(id);
    }

    dispatch(setProjectAnalysisVisibleLayerIds(visibleLayerIds));
  };
};

export const toggleLayerVisibility = id => {
  return (dispatch, getState) => {
    const visibleLayerIds = getState().analysis.single.visibleLayerIds.slice();

    if (visibleLayerIds.indexOf(id) >= 0) {
      visibleLayerIds.splice(visibleLayerIds.indexOf(id), 1);
    } else {
      visibleLayerIds.push(id);
    }

    dispatch(setProjectAnalysisVisibleLayerIds(visibleLayerIds));
  };
};

export const getExports = projectId => {
  return async (dispatch, getState, { apiClient }) => {
    const exports = await apiClient.call(PROJECTS_TAG, 'getProjectExports', { projectId,$limit:10000 });

    dispatch(setProjectExports(exports.data));
  };
};

export const downloadExport = (projectId, id, filename) => {
  return async (dispatch, getState, { apiClient }) => {
    const exports = await apiClient.call(PROJECTS_TAG, 'getProjectExport', { projectId, id });
    download(filename, exports);
  };
};

export const downloadProcessedData = unit => {
  return async (dispatch, getState, { apiClient }) => {
    const data = await apiClient.call(LAYERS_TAG, 'getProcessedData', { id: unit.file });

    const element = document.createElement('a');
    element.setAttribute('href', `data:text/plain;charset=utf-8,${encodeURIComponent(data)}`);
    element.setAttribute('download', unit.file);

    element.style.display = 'none';
    document.body.appendChild(element);

    element.click();

    document.body.removeChild(element);

    dispatch(setProjectAnalysisProcessingStep(null));
    dispatch(setProjectAnalysisIsProcessing(null));
  };
};

export const getProcessData = (unit, analysisModeId) => {
  return async (dispatch, getState, { apiClient }) => {
    let params = { id: unit.file };

    if (
      analysisModeId === 'journeyMap' ||
      analysisModeId === 'aggregateJourneyMap' ||
      analysisModeId === 'circumplex'
    ) {
      params = { ...params, ct: 'csv' };
    }

    const data = await apiClient.call(LAYERS_TAG, 'getProcessedData', params);

    // console.log("data PROCCESS", data)
    dispatch(setProjectAnalysisProcessingStep(null));
    dispatch(setProjectAnalysisIsProcessing(null));

    if (data.length > 0) {
      if (analysisModeId === 'heatMap')
        dispatch(
          updateProjectAnalysisDataHeatmaps(
            data,
            unit.abstract_type_id,
            unit.layer_id,
            unit.stimulus_id
          )
        );
      const activeLayer = getState().analysis.single.activeLayerId;

      if(analysisModeId === 'video') {
        dispatch(updateProjectAnalysisDataVideo(data, activeLayer));
        dispatch(fetchAnnotations(activeLayer,11));
      }
      if (analysisModeId === 'journeyMap') dispatch(updateProjectAnalysisDataJourneyMap(data));
      if (analysisModeId === 'aggregateJourneyMap')
        dispatch(updateProjectAnalysisDataAggregateJourneyMap(data));
      if (analysisModeId === 'circumplex') dispatch(updateProjectAnalysisDataCircumplex(data));
    }
    return data;
  };
};

export const getProcessVideoData = (analysisModeId) => {
  return async (dispatch, getState, { apiClient }) => {

      const activeLayer = getState().analysis.single.activeLayerId;

      if(analysisModeId === 'video') {
        dispatch(updateProjectAnalysisDataVideo(activeLayer));
        dispatch(fetchAnnotations(activeLayer, 11));
      }

  };
};


const registerToSubscription = (feathersClient, subscriptionName, onData) => {
  feathersClient.io.emit('subscriptions', { id: subscriptionName });

  feathersClient.io.on(subscriptionName, onData);
  return () => {
    feathersClient.io.off(subscriptionName);
  };
};

export const getAnalysisDataSet = projectId => {
  return async (dispatch, getState, { apiClient, feathersClient }) => {
    // Set
    dispatch(setProjectAnalysisIsProcessing(true));

    const analysisMode = getState().analysis.single.analysisModes.find(m => m.selected === true);

    if (getState().analysis.data.dataSet && getState().analysis.data.dataSet.length > 0) {
      dispatch(setProjectAnalysisProcessingStep(null));
      dispatch(setProjectAnalysisIsProcessing(null));

      if (analysisMode.id === 'journeyMap')
        dispatch(updateProjectAnalysisDataJourneyMap(getState().analysis.data.dataSet));
      if (analysisMode.id === 'aggregateJourneyMap')
        dispatch(updateProjectAnalysisDataAggregateJourneyMap(getState().analysis.data.dataSet));
      if (analysisMode.id === 'circumplex')
        dispatch(updateProjectAnalysisDataCircumplex(getState().analysis.data.dataSet));
      return;
    }

    if (analysisMode) {
      const subscription = `projects/${projectId}/datasets/${uuidv4()}`;

      const onSubscriptionEnd = registerToSubscription(feathersClient, subscription, payload => {
        if (payload.status === 'inProgress') {
          if (payload.step !== 'extracting_msg_pack') {
            throw new Error('unknown message step in resource dataSets');
          }
          const message = `Querying database: ${payload.progress}%`;
          dispatch(setProjectAnalysisProcessingStep(message));
        } else {
          const status = payload.status.charAt(0).toUpperCase() + payload.status.slice(1);
          dispatch(setProjectAnalysisProcessingStep(status));
        }

        if (payload.status === 'completed' || payload.status === 'fail') {
          onSubscriptionEnd();
          // dispatch(setLayerVisibility(layer.id, true));
        }

        if (payload.status === 'completed') {
          dispatch(setProjectAnalysisProcessingStep(`Getting dataset...`));
          dispatch(getProcessData(payload.units[0], analysisMode.id)).then(data => {
            dispatch(setAnalysisDataSet(data));
          });
        }

        if (payload.status === 'fail') {
          setTimeout(() => {
            // eslint-disable-next-line no-undef
            alert(payload.error); // eslint-disable-line no-alert
          }, 1000);
          dispatch(setProjectAnalysisProcessingStep(null));
          dispatch(setProjectAnalysisIsProcessing(null));
        }
      });

      const createDataSet = async () => {
        const signals = flatMap(getState().analysis.single.signals, s => {
          if (s.name === 'Pupil') return s.id;
          if (s.name === 'EDA') return s.id;

          if (s.name === 'Emotion') {
            return s.dimensions
              .filter(d => ['Arousal', 'Valence_positive', 'Valence_negative'].includes(d.name))
              .map(d => d.abstract_type_id);
          }
          return [];
        });

        try {
          await apiClient.call(
            PROJECTS_TAG,
            'createDataSet',
            { projectId },
            {
              subscription,
              signals
            }
          );
        } catch (e) {
          // eslint-disable-next-line no-undef
          alert(e.response.body.message); // eslint-disable-line no-alert
          dispatch(setProjectAnalysisIsProcessing(false));
        }
      };

      if (!getState().analysis.single.signals || getState().analysis.single.signals.length === 0) {
        dispatch(fetchProjectAnalysisSignals(projectId)).then(createDataSet);
      } else {
        createDataSet();
      }
    }
  };
};

export const startLayerProcess = (layer, stimuliId) => {
  return async (dispatch, getState, { apiClient, feathersClient }) => {
    // Set
    const analysisMode = getState().analysis.single.analysisModes.find(m => m.selected === true);

    // if (analysisMode.id === 'circumplex' && getState().analysis.data.circumplex.canItBeRender) {
    //   console.log(
    //     'jf-debug-> "getState().analysis.data.circumplex": ',
    //     getState().analysis.data.circumplex
    //   );
    //   dispatch(setLayerVisibility(layer.id, true));
    //   dispatch(setProjectAnalysisDataCircumplex(getState().analysis.data.circumplex));
    //   dispatch(setProjectAnalysisProcessingStep(null));
    //   dispatch(setProjectAnalysisIsProcessing(null));
    //   return;
    // }

    if (analysisMode) {
      if (analysisMode.id === 'dataExportation') {
        try {
          await apiClient.call(
            LAYERS_TAG,
            'processLayer',
            { id: layer.id },
            {
              extract: true,
              windowStep: 1000,
              windowSize: 2000
            }
          );
          dispatch(getExports(layer.project_id));
        } catch (e) {
          alert(e.response.body.message); // eslint-disable-line no-undef, no-alert
          dispatch(setProjectAnalysisIsProcessing(false));
        }
      } 
      else if(analysisMode.id === 'video'){
        dispatch(getProcessData(unit, analysisMode.id));
      }
      else {
        dispatch(setProjectAnalysisIsProcessing(true));
        const streamingFiles = {};

        const subscription = `layers/${layer.id}/process/${uuidv4()}`;

        feathersClient.io.emit('subscriptions', { id: subscription });

        feathersClient.io.on(subscription, payload => {
          const status = payload.status.charAt(0).toUpperCase() + payload.status.slice(1);
          if (payload.status === 'inProgress') {
            let message;
            switch (payload.step) {
              case 'extracting_msg_pack':
                message = `Querying database: ${payload.progress}%`;
                break;
              case 'creating_json':
                message = `In Progress - Creating JSON: ${payload.progress}%`;
                break;
              case 'streaming_file':
                {
                  const files = [];
                  streamingFiles[payload.additionalData.fileName] = parseFloat(payload.progress);
                  Object.keys(streamingFiles).forEach(file => {
                    if (streamingFiles[file] !== 100) {
                      files.push(
                        <p key={file}>
                          {file.slice(0, 8)}: {streamingFiles[file]}%
                        </p>
                      );
                    }
                  });
                  message = (
                    <div>
                      Building {analysisMode.label}
                      {files}
                    </div>
                  );
                }
                break;
              default:
                message = '';
            }
            dispatch(setProjectAnalysisProcessingStep(message));
          } else {
            dispatch(setProjectAnalysisProcessingStep(status));
          }

          if (payload.status === 'completed' || payload.status === 'fail') {
            feathersClient.io.off(subscription);
            dispatch(setLayerVisibility(layer.id, true));
          }

          if (payload.status === 'completed') {
            dispatch(setProjectAnalysisProcessingStep(`Rendering ${analysisMode.label}...`));

            each(payload.units, unit => {
              if (analysisMode.id === 'dataExportation') {
                dispatch(downloadProcessedData(unit));
              } else {
                dispatch(getProcessData(unit, analysisMode.id));
              }
            });
          }

          if (payload.status === 'fail') {
            setTimeout(() => {
              // eslint-disable-next-line no-undef
              alert(payload.error); // eslint-disable-line no-alert
            }, 1000);
            dispatch(setProjectAnalysisProcessingStep(null));
            dispatch(setProjectAnalysisIsProcessing(null));
          }
        });

        const addParameters = VisualisationsHelper.getBlockParameters(analysisMode.id, stimuliId);
        try {
          await apiClient.call(
            LAYERS_TAG,
            'processLayer',
            { id: layer.id },
            {
              flood_level: 54,
              extract: analysisMode.id === 'dataExportation',
              subscription,
              ...addParameters
            }
          );
        } catch (e) {
          // eslint-disable-next-line no-undef
          alert(e.response.body.message); // eslint-disable-line no-alert
          dispatch(setProjectAnalysisIsProcessing(false));
        }
      }
    }
  };
};

export const layerSortingUpHandler = () => {
  return (dispatch, getState) => {
    const layersSorting = getState().analysis.single.layersSorting.slice();
    const { activeLayerId } = getState().analysis.single;
    const activeLayerIndex = layersSorting.indexOf(activeLayerId);

    if (activeLayerId === null || activeLayerIndex === 0) {
      return;
    }

    const itemToSwap = layersSorting[activeLayerIndex];
    const toIndex = activeLayerIndex - 1;

    layersSorting[activeLayerIndex] = layersSorting[toIndex];
    layersSorting[toIndex] = itemToSwap;

    dispatch(setProjectAnalysisLayersSorting(layersSorting));
  };
};

export const layerSortingDownHandler = () => {
  return (dispatch, getState) => {
    const layersSorting = getState().analysis.single.layersSorting.slice();
    const { activeLayerId } = getState().analysis.single;
    const activeLayerIndex = layersSorting.indexOf(activeLayerId);

    if (activeLayerId === null || activeLayerIndex === layersSorting.length - 1) {
      return;
    }

    const itemToSwap = layersSorting[activeLayerIndex];
    const toIndex = activeLayerIndex + 1;

    layersSorting[activeLayerIndex] = layersSorting[toIndex];
    layersSorting[toIndex] = itemToSwap;

    dispatch(setProjectAnalysisLayersSorting(layersSorting));
  };
};



// WEBPACK FOOTER //
// ./src/scenes/analysis/data/analysis/layersDispatchers.js